// Pure conversation projection: durable transcript turns plus the one ephemeral live assistant turn.

import { reconcileLiveActivity } from './chat-activity.ts';
import type {
  ChatActivityItem,
  ChatPlanView,
  ChatTranscriptTurn,
  ChatTranscriptView,
} from './projections.ts';

/** One ordered, guard-cleared part of the ephemeral assistant turn. Arrival order is the presentation
 *  order; a terminal activity updates its existing part in place instead of being re-appended. */
export type ChatTurnPart =
  | { readonly type: 'text'; readonly text: string }
  // Ephemeral only: fixed live phases or a server-sanitised provider summary. Durable summaries remain
  // `thinking_summary` activity rows on the owned transcript.
  | { readonly type: 'reasoning'; readonly text: string }
  | { readonly type: 'activity'; readonly activity: ChatActivityItem };

export type ChatTurn = ChatTranscriptTurn & {
  readonly activity?: readonly ChatActivityItem[];
  /** Present only for the active stream: exact server publication order, never regrouped by part kind. */
  readonly parts?: readonly ChatTurnPart[];
  readonly pendingRunId?: string;
};

export interface ChatStreamState {
  readonly runId: string | null;
  readonly text: string;
  /** The ONE latest-wins sentence shown in the live Reasoning row. It is not transcript prose. */
  readonly reasoningText: string | null;
  /** Real work progress outranks stock phase copy; a provider summary outranks both until reconciliation. */
  readonly reasoningSource: 'status' | 'activity' | 'provider_summary' | null;
  /** Latest persist-first Agent plan observed on this live run. */
  readonly plan: ChatPlanView | null;
  /** Text/activity chronology only. The single reasoning row is projected from `reasoningText`. */
  readonly parts: readonly ChatTurnPart[];
}

/** One canonical empty preview so every controller resets the live reasoning scalar as well as its parts. */
export function emptyChatStream(runId: string | null = null): ChatStreamState {
  return { runId, text: '', reasoningText: null, reasoningSource: null, plan: null, parts: [] };
}

/** Latest persisted plan wins; unlike answer text it is scalar current state, not a transcript part. */
export function replaceStreamPlan(state: ChatStreamState, plan: ChatPlanView): ChatStreamState {
  return plan.revision < (state.plan?.revision ?? 0) ? state : { ...state, plan };
}

/** Append a delta to the current trailing text part, or start a new text part after a non-text event. */
export function appendStreamText(state: ChatStreamState, text: string): ChatStreamState {
  if (text.length === 0) return state;
  const last = state.parts[state.parts.length - 1];
  const parts =
    last?.type === 'text'
      ? [...state.parts.slice(0, -1), { type: 'text' as const, text: last.text + text }]
      : [...state.parts, { type: 'text' as const, text }];
  return { ...state, text: state.text + text, parts };
}

/** A finished late-join snapshot is standalone by contract: replace every ephemeral part with its body. */
export function replaceStreamText(state: ChatStreamState, text: string): ChatStreamState {
  return {
    ...state,
    text,
    reasoningText: null,
    reasoningSource: null,
    parts: text.length === 0 ? [] : [{ type: 'text', text }],
  };
}

/** Replace the ONE live sentence in place. A provider summary is already server-sanitised and wins over
 *  the later generic "Finishing" phase; another provider block may still replace it with newer truth. */
export function updateStreamReasoning(
  state: ChatStreamState,
  text: string,
  source: 'status' | 'activity' | 'provider_summary' = 'status',
): ChatStreamState {
  const sentence = text.replace(/\s+/g, ' ').trim();
  if (sentence.length === 0) return state;
  const priority = { status: 0, activity: 1, provider_summary: 2 } as const;
  if (state.reasoningSource !== null && priority[state.reasoningSource] > priority[source])
    return state;
  return { ...state, reasoningText: sentence, reasoningSource: source };
}

/** Project the scalar live status into exactly one visible reasoning part, always before the run timeline. */
export function liveStreamParts(state: ChatStreamState): readonly ChatTurnPart[] {
  return state.reasoningText === null
    ? state.parts
    : [{ type: 'reasoning', text: state.reasoningText }, ...state.parts];
}

/** Append a newly observed tool call, or replace its existing running part at the same array index. */
export function reconcileStreamActivity(
  state: ChatStreamState,
  incoming: ChatActivityItem,
): ChatStreamState {
  // While live, the provider's sanitised one-line summary REPLACES the placeholder sentence rather than
  // becoming a second reasoning row. It remains a normal durable activity row after reconciliation.
  if (incoming.kind === 'thinking_summary') {
    return updateStreamReasoning(state, incoming.label, 'provider_summary');
  }
  // Catalog RUNNING labels have already crossed the server's sanitised client-surface boundary. Project
  // each current action into the same scalar Reasoning row so a tool-using run says what it is doing. The
  // terminal "Checked..." row remains in the activity trace only, avoiding duplicate visible copy.
  const withReasoning =
    incoming.outcome === 'running'
      ? updateStreamReasoning(state, incoming.label, 'activity')
      : state;
  const current = withReasoning.parts
    .filter((part): part is Extract<ChatTurnPart, { type: 'activity' }> => part.type === 'activity')
    .map((part) => part.activity);
  const reconciled = reconcileLiveActivity(current, incoming);
  if (reconciled.length > current.length) {
    return {
      ...withReasoning,
      parts: [...withReasoning.parts, { type: 'activity', activity: incoming }],
    };
  }
  const changed = reconciled.findIndex((row, index) => row !== current[index]);
  if (changed < 0) return withReasoning;
  let activityIndex = 0;
  return {
    ...withReasoning,
    parts: withReasoning.parts.map((part) => {
      if (part.type !== 'activity') return part;
      const index = activityIndex;
      activityIndex += 1;
      return index === changed ? { type: 'activity', activity: reconciled[changed]! } : part;
    }),
  };
}

/** Compose durable history, unresolved human input, and at most one active assistant preview. */
export function composeChatTurns(
  priorRuns: readonly ChatTranscriptView[],
  transcript: ChatTranscriptView | null,
  localTurns: readonly ChatTurn[],
  stream: ChatStreamState,
): readonly ChatTurn[] {
  const prior = priorRuns.flatMap(attachActivity);
  const resolved = transcript === null ? [] : attachActivity(transcript);
  const resolvedRunIds = new Set<string>([
    ...priorRuns.map((run) => run.runId),
    ...(transcript === null ? [] : [transcript.runId]),
  ]);
  const pendingLocal = localTurns.filter(
    (turn) => turn.pendingRunId === undefined || !resolvedRunIds.has(turn.pendingRunId),
  );
  const turns = [...prior, ...resolved, ...pendingLocal];
  const previewParts = liveStreamParts(stream);
  if (
    stream.runId !== null &&
    previewParts.length > 0 &&
    !resolved.some((turn) => turn.author === 'agent')
  ) {
    turns.push({
      id: `streaming-agent-${stream.runId}`,
      author: 'agent',
      text: stream.text,
      parts: previewParts,
    });
  }
  return turns;
}

/** Attach one run's durable activity exactly once, beside its assistant answer. */
function attachActivity(run: ChatTranscriptView): readonly ChatTurn[] {
  const activity = run.activity ?? [];
  return run.turns.map((turn) => {
    if (turn.author !== 'agent') return turn;
    // New snapshots carry the server-authored interleaving. Even an empty `parts` array is authoritative;
    // only an absent field takes the legacy activity-before-answer projection path.
    if (run.parts !== undefined) return { ...turn, parts: run.parts };
    return activity.length > 0 ? { ...turn, activity } : turn;
  });
}
